import {useEffect, useMemo, useRef, useState} from 'react'
import {pickupWaitSchedule} from '@/utils/pickupHistory'
import {Navigate, useNavigate, useParams} from 'react-router'
import {isTripFinished, tripDetailsPath} from '@/features/fromAirportArrival/arrivalRouting'
import TrackDriverMap from '@/components/map/TrackDriverMap.jsx'
import EtaHeader from '@/customComponents/trackDriver/EtaHeader.jsx'
import DriverCard from '@/customComponents/trackDriver/DriverCard.jsx'
import WaitingForTrip from '@/customComponents/trackDriver/WaitingForTrip.jsx'
import MapStage from '@/customComponents/trackDriver/MapStage.jsx'
import {useTrackedRide, TRACK_PHASE} from '@/hooks/useTrackedRide'
import {useMyLocation} from '@/hooks/useMyLocation'
import ArrivalResendCard from '@/customComponents/trackDriver/ArrivalResendCard.jsx'
import ArrivalNextSteps from '@/customComponents/fromAirportArrival/ArrivalNextSteps.jsx'
import TripChat from '@/customComponents/trackDriver/TripChat.jsx'
import {isFromAirportTrip} from '@/features/fromAirportArrival/arrivalEligibility'
import {tripFlightRecord} from '@/features/fromAirportArrival/tripFlightRecord'
import {isFlightArrived} from '@/features/fromAirportArrival/flightStatus'
import WaitingTimer from '@/customComponents/trackDriver/WaitingTimer.jsx'
import {useWaitingStatus} from '@/hooks/useWaitingStatus'

/**
 * TRACK DRIVER — `/{tripId}/track-driver`.
 *
 * The passenger's answer to "where is my car". Three screens behind one URL, chosen by the
 * trip's own status rather than by anything the page is told.
 *
 *   waiting      the ride has not started — when it is booked for, and a way to the trip
 *   approaching  the driver is on their way — ETA, map, who is coming
 *   riding       the passenger is aboard — the map, full screen, and nothing else
 *
 * ── ⚠️ THE MAP CAN BE EMPTY AND THAT IS NOT A BUG ─────────────────────────────────
 * Nothing in the API replays the driver's last known position: `TripResource.driver`
 * carries a name and a vehicle and no coordinates, and there is no endpoint to ask. The
 * position only exists as a broadcast, so between opening this page and the driver's next
 * ping — 3 to 10 seconds, or forever if their phone has locked — there is genuinely
 * nothing to draw. `MapStage` says so in words rather than showing an empty map that looks
 * broken.
 *
 * ── Why this is not a `/rides/:id/...` route ──────────────────────────────────────
 * It is a top-level route because the link is emailed. It stays behind `ProtectedRoute`
 * for the same reason: `RefAuthGate` already wraps every route, so a recipient with no
 * session arriving on `?ref=…` is proved and returned here, and one without it is sent to
 * sign in and back. Making the page public would mean anyone with a trip id could watch a
 * stranger's car.
 */
export default function TrackDriver() {
    const {tripId} = useParams()
    const navigate = useNavigate()
    const {trip, phase, drops, route, driver, nextStop} = useTrackedRide(tripId)

    /* The passenger's own position — what the map's camera is centred on. Asked for
       unconditionally rather than gated on a phase, because the prompt is better answered
       while they are reading the page than at the moment the map appears. */
    const me = useMyLocation()

    /*
     * AND SENT ON, so the DRIVER's map can draw a pin on the person they are coming for.
     *
     * ⚠️ ONLY WHILE A DRIVER IS ACTUALLY COMING. Gated on the phase rather than sent
     * from the moment the page loads: on the waiting screen the ride may be days away and
     * nobody is looking, so there is no one to tell and no reason to say anything.
     *
     * ⚠️ THE PIN IS WORTH MOST WHERE THE ADDRESS IS WEAKEST — a terminal frontage, a
     * hotel forecourt, a house whose geocode landed across the street. The driver portal
     * used to draw it on airport pickups alone and now draws it on every direction, which
     * is why this is not gated on `trip_direction` either.
     */
    /* ⚠️ NO LOCATION SHARING HERE. The portal shares from ONE place — `AppLayout` — on one rule: the first ongoing trip's flight has landed and the ride has not started. A page-level copy would share under its own rule and contradict it. The position is still READ here, for this screen's own map. */

    /*
     * ⚠️ FULL SCREEN IS A LATCH, NOT A PHASE. Once a ride starts the map is full screen by
     * definition, but a passenger who expanded it a minute earlier while the driver was
     * still approaching should not have it collapse under them at the moment the driver
     * presses "start" — the interesting thing has just happened.
     */
    const [expanded, setExpanded] = useState(false)

    /*
     * THE ARRIVAL CARDS — "Your next step" and the walk card, the same ones the arrival
     * guide shows (`ArrivalNextSteps`), on a from-airport ride once the flight is down.
     *
     * Landed is read the way the guide reads it: the backend's flight record says arrived,
     * or there is no record yet (an unknown flight counts as down — withholding the walk
     * from someone at the gate is the worse error). A failed lookup is NOT landed.
     */
    const [walkView, setWalkView] = useState(null) // 'map' | 'steps' | 'guide' | null

    /*
     * THE WAITING CLOCK — the same one the driver sees, top-right on the map, while the
     * driver is on the way or at the pickup. A disabled 00:00 until the clock starts (the
     * scheduled pickup, once the driver has marked arrival). Hidden when the fleet has
     * waiting fees switched off (`enabled: false` on the resource).
     */
    const approaching = phase === TRACK_PHASE.approaching
    const waiting = useWaitingStatus(tripId, {enabled: approaching})
    const showWaitingTimer = approaching && waiting.data?.enabled !== false
    const waitingLive = waiting.data?.enabled === false ? null : waiting.data?.live || null
    const [chatOpen, setChatOpen] = useState(false)
    const flightRecord = tripFlightRecord(trip)
    const showArrivalCards = isFromAirportTrip(trip)
        && trip?.flight?.tracking_status !== 'failed'
        && (!flightRecord || isFlightArrived(flightRecord))
    const riding = phase === TRACK_PHASE.riding
    const fullScreen = riding || expanded

    /*
     * ── ⚠️ THE FULL-SCREEN MAP IS FIXED TO THE VIEWPORT, NOT MEASURED INTO IT ─────
     *
     * It used to compute its own height as "the window, minus wherever my top edge is",
     * and every one of that idea's failure modes turned up in practice: the top edge is
     * not final on the first frame, it moves when the page is scrolled, a CSS minimum
     * stretched the box without changing the height its children resolved against, and
     * Mapbox builds its canvas once from whatever number was there at the time. The result
     * was a map that opened as a strip and sometimes stayed one.
     *
     * `fixed inset-0` has no such number. The box is the viewport, it is correct on the
     * first frame, and nothing about the page above it can make it wrong.
     *
     * ⚠️ IT COVERS THE APP HEADER, deliberately. A map asked to be full screen should be
     * full screen; the close control is inside it and is the way back.
     */

    /* A finished ride has nothing to track. Sent to the trip rather than left on a page
       whose entire subject has ended — `replace`, so the browser's back button does not
       bounce them straight back into it. */
    const finished = phase === TRACK_PHASE.finished
    const bouncedRef = useRef(false)
    useEffect(() => {
        if (!finished || bouncedRef.current) return
        bouncedRef.current = true
        navigate(`/rides/${tripId}`, {replace: true})
    }, [finished, navigate, tripId])

    /*
     * ⚠️ MEMOISED, AND IT KEYS ON THE LAYOUT. `RouteMap` re-frames the camera whenever this
     * object's identity changes, so a literal here would re-fit the map on every render —
     * which, with a position arriving every few seconds, is constantly.
     *
     * The insets differ between the two layouts because the chrome does: full screen has
     * the instruction banner floating over the top of the map, and the card does not.
     */
    const fitPadding = useMemo(
        () => (fullScreen ? {top: 96, right: 48, bottom: 48, left: 48} : {top: 40, right: 40, bottom: 40, left: 40}),
        [fullScreen],
    )

    if (phase === TRACK_PHASE.loading) {
        return <div className="min-h-[60vh] animate-pulse rounded-(--radius-xl) bg-(--bg-grey)"/>
    }

    if (phase === TRACK_PHASE.missing) {
        return (
            <div className="white-global-section text-center max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
                <p className="text-(--text-secondary)">This ride could not be found.</p>
            </div>
        )
    }

    // The redirect above is already in flight; rendering the ride again would flash it.
    if (finished) return null

    if (phase === TRACK_PHASE.waiting) {
        return <WaitingForTrip trip={trip} onViewTrip={() => navigate(`/rides/${tripId}`)}/>
    }

    /*
     * ⚠️ NO LOCATION, NO TRACKING — a requirement rather than a preference, and drawn
     * OVER the screen rather than instead of it.
     *
     * The exchange runs both ways: the passenger's position is what puts their pin on the
     * driver's map, and it is the only thing either side has to work out that they have
     * met, because nothing here announces an arrival. A passenger who declines still sees
     * a car and assumes all is well, while the driver looks for someone invisible.
     *
     * ⚠️ AFTER THE WAITING SCREEN, DELIBERATELY. A ride that has not started needs no
     * position from anyone, and a permission prompt days before a booking has no reason a
     * passenger could see.
     */
    /**
     * ⚠️ THE UNDISMISSABLE "SHARE YOUR LOCATION" BLOCK IS GONE (2026-09-22).
     *
     * What it said was true: the exchange runs both ways, the passenger's pin is how the
     * driver finds them, and a passenger who declines sees a car while the driver looks for
     * someone invisible. What it did about that was take the tracking screen away from a
     * customer sitting in a terminal — including one who tapped "block" once, on some other
     * trip, and whom no web API can prompt again. A ride they have paid for is not the
     * lever to win a permission with.
     *
     * Location is now asked for and never demanded, everywhere on the passenger side. The
     * driver's map already says "waiting for your passenger's location" rather than
     * pretending, which is the honest half of this that was worth keeping.
     */
    const gate = null

    const map = (
        <TrackDriverMap
            driver={driver.position}
            drops={drops}
            route={route}
            me={me.position}
            /* The ride's own stored geometry, which is what the map draws until the first
               position lands — see the note in `TrackDriverMap`. */
            bookedPolyline={trip?.route?.polyline || null}
            fitPadding={fitPadding}
            showBanner={fullScreen}
        />
    )

    /*
     * FULL SCREEN — the whole viewport, under the app's own header.
     *
     * ⚠️ THE NEGATIVE MARGINS CANCEL `AppLayout`'s PADDING rather than the layout being
     * changed to accommodate this page. `AppLayout` puts `py-5 px-3 md:p-10` around every
     * route, and a map inset by 40px on a phone is a map in a box. Same trick the driver
     * portal's `TripMapStage` uses, and it keeps the exception local to the one page that
     * wants it instead of adding a second path test to a shared layout.
     */
    if (fullScreen) {
        return (
            <div
                className="fixed inset-0 z-50 overflow-hidden bg-(--bg-white)"
            >
                <MapStage
                    driver={driver}
                    /* ⚠️ ALWAYS A WAY OUT. It used to be `null` while riding, on the reasoning
                       that aboard there is nothing to shrink TO — the driver card is about
                       someone who has already arrived. That part is true, but the
                       conclusion trapped the passenger: full screen is forced while riding,
                       so with no ✕ the only exit was the browser's back button.

                       So the control means different things by phase. Before boarding it
                       collapses back to the card, as it always did. Aboard it LEAVES, to the
                       ride's own page — the one place with something to show that is not
                       this map. */
                    onCollapse={riding
                        ? () => navigate(tripDetailsPath(tripId))
                        : () => setExpanded(false)}
                    full
                >
                    {map}
                </MapStage>
                {gate}
            </div>
        )
    }

    /* ⚠️ BELOW EVERY HOOK ABOVE, and it has to stay there. An early return placed among
       them would skip the ones after it on the renders that redirect — a different number
       of hooks between renders, which is React error #310, not a subtle bug. */
    /* ⚠️ FINISHED FIRST. A cancelled ride can still read as "before onboard" on a stale
       phase, and the arrivals hall is the wrong answer for a ride that is not happening.
       Same destination as the phase-driven bounce further up — the two agree on purpose,
       and this one also covers Cancelled and NoShow, which never reach that phase. */
    if (trip && isTripFinished(trip)) {
        return <Navigate to={tripDetailsPath(tripId)} replace/>
    }

    /* ⚠️ THE HAND-BACK TO /welcome IS GONE, AND `?arrived=1` WITH IT (2026-09-22).
       This used to send any pre-boarding airport ride back to the welcome PAGE, so the two
       screens bounced off each other and the flag was the only way out — set by "I've
       arrived" purely to break the loop. The welcome is a modal now (`WelcomeModal`,
       mounted in `AppLayout`): it opens over whatever page the passenger is on, including
       this one, so there is no page to hand back to and nothing to suppress. A passenger
       who wants it again presses the plane button. */

    return (
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 max-md:gap-3">
            <EtaHeader trip={trip} route={route} nextStop={nextStop} hasPosition={driver.hasPosition}/>

            <MapStage driver={driver} onExpand={() => setExpanded(true)}>
                {map}
                {showWaitingTimer && <WaitingTimer live={waitingLive} schedule={pickupWaitSchedule(trip)} arrived={!!(trip?.reached_pickup?.driver_at || trip?.waypoints?.[0]?.arrived_at)} bare/>}
            </MapStage>

            {gate}

            <DriverCard trip={trip}/>

            {showArrivalCards && (
                <ArrivalNextSteps
                    trip={trip}
                    tripId={tripId}
                    walkView={walkView}
                    onWalkViewChange={setWalkView}
                    onOpenChat={() => setChatOpen(true)}
                    onWalkDone={() => setWalkView(null)}
                />
            )}

            {showArrivalCards && (
                <TripChat
                    tripId={trip?.id}
                    driverName={trip?.driver?.name}
                    driverPhoto={trip?.driver?.photo}
                    open={chatOpen}
                    onClose={() => setChatOpen(false)}
                    onStartWalking={() => { setChatOpen(false); setWalkView('map') }}
                />
            )}

            {/* ⚠️ UNDER THE DRIVER CARD, NOT ABOVE IT. This answers "they still have not
                found me", which is a question that only occurs to someone after they have
                looked at who is coming and how far away they are. It renders itself away on
                any ride without a surveyed booth — see `ArrivalResendCard`. */}
            <ArrivalResendCard trip={trip}/>

        </div>
    )
}
